import { useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence, useAnimationControls } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Music2, VolumeX } from "lucide-react";
import StoriesViewer from "./StoriesViewer";
import type { Tables } from "@/integrations/supabase/types";

interface BackTextStyle {
  fontFamily?: string;
  fontSize?: number;
  color?: string;
  textAlign?: "left" | "center" | "right";
  backgroundColor?: string;
}

interface InteractiveCardProps {
  templateUrl: string;
  title: string;
  messages: Tables<"messages">[];
  contributions: Tables<"contributions">[];
  envelopeTint?: string;
  musicId?: string | null;
  backText?: string | null;
  backTextStyle?: BackTextStyle | Record<string, unknown> | null;
  templateMusicUrl?: string | null;
  templateMusicAutoplay?: boolean | null;
}

function extractYouTubeId(url: string | null | undefined): string | null {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/))([\w-]{6,})/);
  return m?.[1] ?? null;
}

const POS_TUCKED = "12%";
const EASE_UP = [0.22, 1, 0.36, 1] as const;

const InteractiveCard = ({ templateUrl, title, messages, contributions, envelopeTint, musicId, backText, backTextStyle, templateMusicUrl, templateMusicAutoplay }: InteractiveCardProps) => {
  const [hasAnimatedIntro, setHasAnimatedIntro] = useState(false);
  const [showStories, setShowStories] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [overlayPage, setOverlayPage] = useState<0 | 1>(0);

  // Background music — pulled from the card template. We embed YouTube
  // via an iframe so the recipient hears the chosen track when the card
  // overlay is open. `musicMuted` lets the recipient silence it from
  // a small floating control even when autoplay is on.
  const ytId = extractYouTubeId(templateMusicUrl);
  const autoplayPref = templateMusicAutoplay ?? true;
  const [musicMuted, setMusicMuted] = useState<boolean>(!autoplayPref);

  const controls = useAnimationControls();

  const envelopeFilterStyle = envelopeTint || undefined;
  const hasBackText = !!(backText && backText.trim());
  const bts = (backTextStyle || {}) as BackTextStyle;
  const backStyle: React.CSSProperties = {
    backgroundColor: (bts.backgroundColor as string) || "#ffffff",
    color: (bts.color as string) || "#1a1a1a",
    fontFamily: bts.fontFamily ? `"${bts.fontFamily}", serif` : 'Georgia, serif',
    fontSize: `${(bts.fontSize as number) || 22}px`,
    textAlign: (bts.textAlign as React.CSSProperties["textAlign"]) || "center",
    lineHeight: 1.45,
    whiteSpace: "pre-wrap",
  };

  const handleToggle = () => {
    if (!hasAnimatedIntro) return;
    setOverlayPage(0);
    setOverlayOpen(true);
  };

  return (
    <>
      {/* Background music — mounted at the page level so it begins
          playing as soon as the guest lands on the card. Browsers block
          unmuted autoplay, so we start muted when autoplay is on and
          let the recipient unmute via the floating control. */}
      {ytId && autoplayPref && createPortal(
        <iframe
          key={`bg-${ytId}-${musicMuted ? "m" : "u"}`}
          src={`https://www.youtube.com/embed/${ytId}?autoplay=1&controls=0&loop=1&playlist=${ytId}&mute=${musicMuted ? 1 : 0}&playsinline=1`}
          title="Card music"
          allow="autoplay; encrypted-media"
          className="fixed opacity-0 pointer-events-none"
          style={{ width: 1, height: 1, left: -10, top: -10, zIndex: -1 }}
        />,
        document.body
      )}
      {/* Fixed-size self-contained wrapper — never changes height */}
      <div
        className="relative mx-auto select-none cursor-pointer overflow-hidden"
        style={{ width: "115%", maxWidth: 640, aspectRatio: "500/720", marginLeft: "-7.5%", marginRight: "-7.5%" }}
        onClick={handleToggle}
      >
        {/* Envelope + card container pinned to bottom */}
        <div className="absolute bottom-0 left-0 right-0" style={{ aspectRatio: "500/720" }}>
          {/* Card element — slides up from below into envelope on intro, then idle bounces */}
          <motion.div
            className="absolute left-1/2 z-[1]"
            style={{
              width: "78%",
              x: "-50%",
              aspectRatio: "360/508",
              opacity: overlayOpen ? 0 : 1,
            }}
            initial={{ bottom: POS_TUCKED, rotate: 15 }}
            animate={hasAnimatedIntro ? controls : { bottom: POS_TUCKED, rotate: 0 }}
            transition={
              !hasAnimatedIntro
                ? { delay: 0.9, duration: 1.2, ease: EASE_UP }
                : undefined
            }
            onAnimationComplete={() => {
              if (!hasAnimatedIntro) {
                setHasAnimatedIntro(true);
                controls.start({
                  bottom: [POS_TUCKED, "15%", POS_TUCKED, "14%", POS_TUCKED],
                  transition: { duration: 1.6, ease: "easeInOut", repeat: Infinity, repeatDelay: 3 },
                });
              }
            }}
          >
            <div
              className="w-full h-full overflow-hidden bg-white rounded-sm"
              style={{ boxShadow: "0 2px 8px -1px rgba(0,0,0,0.08), 0 1px 3px 0 rgba(0,0,0,0.04)" }}
            >
              {templateUrl && (
                <img src={templateUrl} alt="Card" className="w-full h-full object-cover" />
              )}
              {title && !templateUrl && (
                <div className="flex items-center justify-center h-full p-6">
                  <h3 className="text-2xl font-bold text-center text-foreground">{title}</h3>
                </div>
              )}
            </div>
          </motion.div>

          {/* Envelope back */}
          <img
            src="/images/envelope-back.png"
            alt=""
            className="absolute left-0 right-0 bottom-0 w-full pointer-events-none"
            style={{ zIndex: 0, filter: envelopeFilterStyle }}
          />

          {/* Envelope front */}
          <img
            src="/images/envelope-front.png"
            alt=""
            className="absolute left-0 right-0 bottom-0 w-full pointer-events-none"
            style={{ zIndex: 8, filter: envelopeFilterStyle }}
          />

          {/* Tap hint */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: overlayOpen ? 0 : 1 }}
            transition={{ delay: hasAnimatedIntro ? 0 : 2.2 }}
            className="absolute inset-0 flex items-end justify-center pb-[2%] z-10 pointer-events-none"
          >
            <span className="text-[10px] text-muted-foreground/60 font-medium tracking-wide animate-pulse">
              Tap to open
            </span>
          </motion.div>

          {/* Music mute toggle — anchored to bottom-right edge of envelope */}
          {ytId && autoplayPref && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setMusicMuted((m) => !m); }}
              className="absolute w-9 h-9 rounded-full bg-black/55 hover:bg-black/75 backdrop-blur-md text-white flex items-center justify-center shadow-lg"
              style={{ zIndex: 9, right: "4%", bottom: "2%" }}
              aria-label={musicMuted ? "Unmute music" : "Mute music"}
            >
              {musicMuted ? <VolumeX className="w-4 h-4" /> : <Music2 className="w-4 h-4" />}
            </button>
          )}
        </div>
      </div>

      {/* Full-screen overlay preview — opens on tap, closes back into envelope */}
      {createPortal(
        <AnimatePresence>
          {overlayOpen && (
            <motion.div
              key="card-overlay"
              className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setOverlayOpen(false)}
            >
              <motion.button
                type="button"
                onClick={(e) => { e.stopPropagation(); setOverlayOpen(false); }}
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white flex items-center justify-center z-10"
                aria-label="Close"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { delay: 0.2 } }}
                exit={{ opacity: 0 }}
              >
                <X className="w-5 h-5" />
              </motion.button>

              {ytId && !autoplayPref && (
                <>
                  {/* Autoplay disabled at template level — start music
                      only when the recipient opens the card. */}
                  <iframe
                    key={`${ytId}-${musicMuted ? "m" : "u"}`}
                    src={`https://www.youtube.com/embed/${ytId}?autoplay=1&controls=0&loop=1&playlist=${ytId}&mute=${musicMuted ? 1 : 0}&playsinline=1`}
                    title="Card music"
                    allow="autoplay; encrypted-media"
                    className="absolute opacity-0 pointer-events-none"
                    style={{ width: 1, height: 1, left: -10, top: -10 }}
                  />
                  <motion.button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setMusicMuted((m) => !m); }}
                    className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white flex items-center justify-center z-10"
                    aria-label={musicMuted ? "Unmute music" : "Mute music"}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { delay: 0.2 } }}
                    exit={{ opacity: 0 }}
                  >
                    {musicMuted ? <VolumeX className="w-5 h-5" /> : <Music2 className="w-5 h-5" />}
                  </motion.button>
                </>
              )}

              <motion.div
                className="overflow-hidden bg-white rounded-md shadow-2xl relative"
                style={{ aspectRatio: "360/508", maxHeight: "92vh", maxWidth: "min(92vw, 520px)", perspective: 1600 }}
                initial={{ scale: 0.3, opacity: 0, y: 40 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.3, opacity: 0, y: 40 }}
                transition={{ type: "spring", stiffness: 220, damping: 26 }}
                onClick={(e) => e.stopPropagation()}
              >
                <motion.div
                  className="relative w-full h-full"
                  style={{ transformStyle: "preserve-3d" }}
                  animate={{ rotateY: overlayPage === 0 ? 0 : 180 }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                >
                  {/* Front */}
                  <div className="absolute inset-0 w-full h-full" style={{ backfaceVisibility: "hidden" }}>
                    {templateUrl ? (
                      <img src={templateUrl} alt="Card" className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex items-center justify-center h-full w-full p-8">
                        <h3 className="text-3xl font-bold text-center text-foreground">{title}</h3>
                      </div>
                    )}
                  </div>
                  {/* Back */}
                  <div
                    className="absolute inset-0 w-full h-full p-8 flex items-center justify-center"
                    style={{ ...backStyle, backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
                  >
                    {hasBackText ? backText : <span className="opacity-60 text-base">No back-side message.</span>}
                  </div>
                </motion.div>

                {hasBackText && (
                  <>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setOverlayPage((p) => (p === 0 ? 1 : 0)); }}
                      className="absolute bottom-3 right-3 z-10 px-3 h-9 rounded-full bg-black/60 hover:bg-black/75 text-white text-xs font-medium flex items-center gap-1 backdrop-blur-md shadow-lg"
                      aria-label={overlayPage === 0 ? "Show back" : "Show front"}
                    >
                      {overlayPage === 0 ? (<><span>Read message</span><ChevronRight className="w-4 h-4" /></>) : (<><ChevronLeft className="w-4 h-4" /><span>Back to card</span></>)}
                    </button>
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${overlayPage === 0 ? "bg-white" : "bg-white/40"}`} />
                      <span className={`w-1.5 h-1.5 rounded-full ${overlayPage === 1 ? "bg-white" : "bg-white/40"}`} />
                    </div>
                  </>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Stories viewer */}
      {createPortal(
        <AnimatePresence>
          {showStories && (
            <StoriesViewer
              templateUrl={templateUrl}
              title={title}
              messages={messages}
              contributions={contributions}
              onClose={() => setShowStories(false)}
              musicId={musicId}
            />
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default InteractiveCard;
